/**
 * DrawRequestModal — RM/TL/ASM submits a request for a cash advance against
 * future commissions. Lands in Chad's approval queue (Finance Board → Draws).
 *
 * Spec: memory/features/finance-board-redesign.md (Ship 6, §5)
 */

import { useState } from "react";
import { supabase } from "../../lib/supabase";
import { notifyChadOnDrawRequest } from "../../lib/notifyChadOnDrawRequest";
import { fmtUSD } from "../../lib/analytics";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import { useAuth } from "../../context/AuthContext";

export default function DrawRequestModal({
  currentBalance,
  requesterName,
  requesterRole,
  onClose,
  onCreated,
}: {
  currentBalance: number;
  requesterName: string;
  requesterRole: string;
  onClose: () => void;
  onCreated?: () => void;
}) {
  const { member } = useAuth();
  const [amount, setAmount] = useState<string>("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      setError("Amount required.");
      return;
    }
    if (reason.trim().length < 5) {
      setError("Reason required (min 5 chars).");
      return;
    }
    setError(null);
    setSubmitting(true);

    try {
      // team_member_id + requested_by reference team_roster(id), not auth.users(id).
      // FIXME: the cd_insert RLS policy is `requested_by = auth.uid()` while the
      // FK is to team_roster(id). Both conditions can't be satisfied at once
      // until the policy is migrated to resolve auth user → team_roster.id by
      // email. Tracking in CLAUDE.md follow-ups (same shape as pa_insert).
      if (!member?.id) throw new Error("Not signed in.");
      const memberId = member.id;

      const { data: row, error: insertErr } = await supabase
        .from("commission_draws")
        .insert({
          recipient_kind: "team_member",
          team_member_id: memberId,
          requested_amount: amt,
          requested_by: memberId,
          request_reason: reason.trim(),
          approval_status: "pending",
        })
        .select("id")
        .single();
      if (insertErr) throw insertErr;

      // Fire-and-forget — Chad gets SMS + in-portal notification
      notifyChadOnDrawRequest({
        draw_id: row.id,
        requester_name: requesterName,
        requester_role: requesterRole,
        amount: amt,
        reason: reason.trim(),
      });

      onCreated?.();
      onClose();
    } catch (err) {
      setError(formatSupabaseError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-3 border-b border-slate-100">
          <h2 className="text-base font-semibold text-bardin-ink">Request a draw</h2>
          <p className="text-xs text-slate-500">
            Cash advance against your next commission payouts. Chad will review and approve.
          </p>
        </div>

        <div className="px-5 py-4 space-y-4">
          {currentBalance > 0 && (
            <div className="bg-purple-50/40 border border-purple-200 rounded-lg p-3">
              <div className="text-xs text-purple-700 font-bold uppercase tracking-wide">Your current outstanding balance</div>
              <div className="text-xl font-extrabold text-purple-700 mt-1">{fmtUSD(currentBalance)}</div>
              <div className="text-[11px] text-slate-600 mt-1">
                Your next commission Bills will be reduced by this amount until repaid.
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-600 mb-1">
              Amount requested
            </label>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="w-full text-sm border border-slate-300 rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-600 mb-1">
              Reason <span className="text-red-600">(required)</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="e.g. Truck repair, medical co-pay, etc."
              className="w-full text-sm border border-slate-300 rounded px-3 py-2"
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Chad sees this on his approval card. Be specific so he can approve fast.
            </div>
          </div>

          {error && <div className="text-xs text-red-700 bg-red-50 rounded px-3 py-2">⚠ {error}</div>}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50">
          <button onClick={onClose} disabled={submitting} className="px-4 py-1.5 rounded text-sm text-slate-600 hover:text-slate-800 disabled:opacity-50">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting || !amount || reason.trim().length < 5}
            className="px-4 py-1.5 rounded bg-bardin-orange text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? "Submitting…" : "Send to Chad"}
          </button>
        </div>
      </div>
    </div>
  );
}
